import { Alignment, Spacing, ThemeProvider, Typography } from "@bonusx-global/bx-design-system";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import debounce from "lodash/debounce";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProductCacheTimestamp } from "../../hooks/use-product-cache-timestamp/useProductCacheTimestamp";
import { useAccount } from "../../hooks/useAccount";
import { userSubscriptionService } from "../../services/user-subscription/user-subscription.instance";
import { BackdropOnTop } from "./components/backdrop-on-top";
import { CreditCardConvertDuotoneBig } from "./components/credit-card-convert-duotone-big";
import { SubscriptionCheckoutQueryString } from "./entities/subscription-checkout-query-string.enum";
import { afterUpgradeEvents } from "./hooks/payment-backdrop-event";
import { sendGtmEventAfterUpgrade } from "./hooks/send-gtm-event-after-upgrade";
import { useIsBackFromSubscriptionCheckout } from "./hooks/use-is-back-from-subscription-checkout";

export const UPGRADE_STATUS_SUCCEEDED = "succeeded";
const WAIT_CHARGEBEE_CALLBACK = 5_000;

function debouncePurchaseEvent() {
  const waitTime = 300;

  return debounce(function trigger(action: () => Promise<void>) {
    action().then().catch(console.error);
  }, waitTime);
}

const purchaseEventDebounced = debouncePurchaseEvent();

export function PaymentBackdrop({ children }: Readonly<PropsWithChildren<{}>>): JSX.Element {
  const isBackFromCheckout = useIsBackFromSubscriptionCheckout();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { accountData } = useAccount();
  const [, setProductCacheTimestamp] = useProductCacheTimestamp();

  useEffect(
    function showWhenUpgradeIsSucceeded() {
      const isPaymentProcessActive = isBackFromCheckout && !!accountData;
      if (!isPaymentProcessActive) {
        return;
      }
      setProductCacheTimestamp(new Date().getTime());

      const timeout = setTimeout(function afterFewMoments() {
        const url = new URL(window.location.href);
        const subscriptionState = url.searchParams.get(SubscriptionCheckoutQueryString.STATE);

        const sendGtmEvent = sendGtmEventAfterUpgrade.bind(null, accountData, url);
        const afterUpgrade = afterUpgradeEvents.bind(null, url, navigate, locationState);

        if (subscriptionState === UPGRADE_STATUS_SUCCEEDED) {
          const action = () => {
            return userSubscriptionService.getSubscriptionAccount().then(sendGtmEvent).finally(afterUpgrade);
          };

          purchaseEventDebounced(action);
        }
      }, WAIT_CHARGEBEE_CALLBACK);

      return function cleanup() {
        clearTimeout(timeout);
      };
    },
    [isBackFromCheckout, accountData, navigate, locationState]
  );

  return (
    <>
      <BackdropOnTop open={isBackFromCheckout}>
        <ThemeProvider>
          <Container>
            <Grid
              container
              gap={Spacing[500]}
              justifyContent={Alignment.center}
              alignContent={Alignment.center}
              textAlign={Alignment.center}
            >
              <Grid item xs={12}>
                <CreditCardConvertDuotoneBig />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h5">Pagamento in elaborazione</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {"L'aggiornamento del tuo piano potrebbe richiedere alcuni minuti."}
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </ThemeProvider>
      </BackdropOnTop>
      {!isBackFromCheckout && children}
    </>
  );
}
